import React, { useEffect } from 'react';
import { View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveLocation } from "@/data/locations";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const EMPTY_DRAFT = {};

export default function AddLocationModal() {
    const {id, fromScreen, fromStoreKey} = useLocalSearchParams();
    const storeKey = id || "new"

    const router = useRouter()
    const queryClient = useQueryClient()
    const pathname = usePathname();
    const {data: locationToEdit} = Query.locations.byId({id: id, options: {enabled: !!id, placeholderData: {}}})

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const locationDetails = useEditScreenStore(s => s.AddLocationModal?.[storeKey]) || EMPTY_DRAFT

    const saveLocationMutation = useMutation({
        mutationFn: saveLocation,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.locations.all()});

            removeEditScreenData("AddLocationModal", storeKey)

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddLocationModal")
            }
        },
        onError: (err) => {
            console.debug(err)
        },
    })


    /*** Effects ***/
    useEffect(() => {
        if(locationToEdit && !Object.keys(locationDetails).length){
            setLocationDetails({
                ...locationToEdit,
                originalValues: locationToEdit,
            })
        }
    }, [locationToEdit]);

    const setLocationDetails = (details) => {
        setEditScreenData("AddLocationModal", storeKey, details)
    }
    
    return (
        <ModalWrapper
            onCancel={() => removeEditScreenData("AddLocationModal", storeKey)}
            onConfirm={() => {
                const { originalValues, ...cleanDetails } = locationDetails;
                saveLocationMutation.mutate({details: cleanDetails, originalValues})
            }} 
            title={id ? "Update Location" : "New Location" }
        >
        
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={{alignItems: "center"}}>
                <View style={{paddingBottom:20, width:"100%"}}>
                    <InputSection description="The Location Name is what the location is called (e.g. Main Sanctuary, Gymnasium, Room 204). Use the Description for extra details like directions, sections, or notes.">
                    
                            <Input
                                title="Location Name"
                                placeholder="e.g. Main Sanctuary, Gymnasium, Room 204"
                                maxLength={120}
                                value={locationDetails.name}
                                onUpdate={(new_value) => {
                                    setLocationDetails({
                                        name: new_value
                                    })
                                }}
                            />

                            <Input
                                title="Description"
                                placeholder="e.g. Directions, section, or other details"
                                value={locationDetails.detailedArea}
                                onUpdate={(new_value) => {
                                    setLocationDetails({
                                        detailedArea: new_value
                                    })
                                }}
                            />

                            <SheetSelector type="addresses" title="Address" fromScreen={pathname} fromStoreKey={storeKey} addScreenName="addresses/create" value={locationDetails.Address} onChange={(selected_item) => {
                                setLocationDetails({
                                    addressID: selected_item?.id,
                                    Address: selected_item
                                });
                            }} />
                    </InputSection>
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}